<template lang="pug">
include ../pug/svg
section#assignment.group
	div#ctrl
		div
			Search(v-model:search="search")
			Filter(v-if="groupFilters.length" id="groupFilter" :label="terminology('user','group','singular')" :title="terminology('user','group','singular')" :options="groupFilters" v-model:selected="groupFilter")
			button.but.pri.spinner(:class="{spin:saving}" type="button" :disabled="saving||!canSave" @click="saveAssignment") Save
				+svg(svg-filename="spinner" aria-hidden="true" alt="Processing")
			button.but.pri.spinner(:class="{spin:resetting}" type="button" :disabled="resetting||!canCancel" @click="resetAssignment") Cancel
				+svg(svg-filename="spinner" aria-hidden="true" alt="Processing")
	div.wrapper
		h6
			button.but.circ.dblfwdarr.text(type="button" @click="assignUsers(unassignedUsers)" :disabled="!unassignedUsers.length")
				span Assign All
				+svg(svg-filename="iconDoubleArrow" aria-hidden="true" alt="Assign all icon")
			span Unassigned ({{unassignedUsers.length}})
		h6
			span Assigned ({{assignedUsers.length}})
			button.but.circ.dblbckarr.text.flip(type="button" @click="unassignUsers(assignedUsers)" :disabled="!assignedUsers.length")
				span Unassign All
				+svg(svg-filename="iconDoubleArrow" aria-hidden="true" alt="Unassign all icon")
	div.wrapper
		div.listbg
			ul.list.unassigned
				li(v-for="user in unassignedUsers")
					picture.user(v-if="user.profileUrl" :class="{online:user.online}")
						img(:src="user.profileUrl" width="40" height="40" :alt="user.fullName")
					span.user(v-else :class="{online:user.online}") {{user.initials}}
					div.hgroup
						h2 {{user.fullName}}
						h3 {{terminology('user', user.userOrganisations[0].role.slug,'singular')}}
							span.email {{user.email}}
					button.but.circ.fwdarr(type="button" @click="assignUsers(user)")
						span Assign User
						+svg(svg-filename="iconArrow" aria-hidden="true" alt="Assign icon")
		div.listbg
			ul.list.assigned
				li(v-for="user in assignedUsers")
					picture.user(v-if="user.profileUrl" :class="{online:user.online}")
						img(:src="user.profileUrl" width="40" height="40" :alt="user.fullName")
					span.user(v-else :class="{online:user.online}") {{user.initials}}
					div.hgroup
						h2 {{user.fullName}}
						h3 {{terminology('user', user.userOrganisations[0].role.slug,'singular')}}
							span.email {{user.email}}
					button.but.circ.bckarr(type="button" @click="unassignUsers(user)")
						span Unassign User
						+svg(svg-filename="iconArrow" aria-hidden="true" alt="Unassign icon")
	ModalConfirm(:active="leaveTo" :useDrag="true" @cancel="leaveTo=null" @confirm="resetAssignment")
</template>
<script>
import OrganisationService from '../services/OrganisationService';
import Search from '../components/Search';
import Filter from '../components/Filter';
import ModalConfirm from '../components/ModalConfirm';
import { mapMutations } from 'vuex';

export default {
	name: 'ManageGroupAssignment',
	components: {
		Search,
		Filter,
		ModalConfirm,
	},
	data() {		
		return {
			groupId: parseInt(this.$route.params.groupId),
			search: null,
			groupFilter: null,
			canSave: false,
			canCancel: false,
			saving: false,
			resetting: false,
			leaveTo: null,
		};
	},
	mounted() {
		this.$store.dispatch('gui/setHeader', {
			title: this.terminology('user','group','singular') + ' Assignment (' + this.group.name + ')',
			backRoute: {
				text: this.terminology('user','group','plural'),
				name: 'ManageGroup',
			},
		});
	},
	beforeRouteLeave (to, from, next) {
		// unsaved changes
		if (this.canSave && !this.leaveTo) {
			this.leaveTo = to;
			this.$store.dispatch('gui/setTintState', true);
			next(false);
			
		} else {
			next();			
		}
	},
	computed: {
		group() {
			const groups = this.$store.state.gui.organisation.organisationGroups;
			return groups.find(g => g.id === this.groupId);
		},
		users() {
			return [...this.$store.state.users.users].sort((a, b) => a.fullName.localeCompare(b.fullName));
		},
		filteredUsers() {
			// remove archived users
			let activeUsers = this.users.filter(u => u.userOrganisations[0].status !== 'archived');
			
			// apply user group filter
			const groupSelected = this.groupFilter?.value || null;
			activeUsers = (groupSelected) ? activeUsers.filter(u => u.userOrganisations[0].userOrganisationGroups.map(g => g.organisationGroupId).includes(groupSelected)) : activeUsers;
			
			// apply serch term filter
			return (this.search) ? activeUsers.filter(u => u.fullName.toLowerCase().includes(this.search.toLowerCase())) : activeUsers;
		},
		assignedUsers() {
			return this.filteredUsers.filter(u => u.userOrganisations[0].userOrganisationGroups.find(ug => ug.organisationGroupId === this.groupId));
		},
		unassignedUsers() {
			return this.filteredUsers.filter(u => !u.userOrganisations[0].userOrganisationGroups.find(ug => ug.organisationGroupId === this.groupId));
		},
		groupFilters() {
			const groups = this.$store.state.gui.organisation.organisationGroups;
			// map value/option pairs to group data
			return groups.map(g => {
				return { value: g.id, option: g.name };
			});
		},
	},
	methods: {
		...mapMutations('users', ['addToGroup','removeFromGroup']),
		terminology(groupKey, termKey, varKey) {
			return this.$store.getters['gui/customTerm'](groupKey, termKey, varKey);
		},
		assignUsers(user) {
			this.canSave = true;
			this.canCancel = true;
			
			this.addToGroup({
				user,
				group: this.group,
			});
		},
		unassignUsers(user) {
			this.canSave = true;
			this.canCancel = true;
			
			this.removeFromGroup({
				user,
				groupId: this.groupId,
			});
		},
		async saveAssignment() {
			try {
				this.saving = true;
				this.canSave = false;
				this.canCancel = false;
				
				const delay = this.$store.getters['gui/delay'];
				
				// save assignments to db
				const userOrganisationIds = this.assignedUsers.map((u) => {
					return u.userOrganisations[0].id;
				});
				
				await OrganisationService.updateGroupAssignment({
					groupId: this.groupId,
					userOrganisationIds,
				});
				
				await delay;
				
				this.saving = false;
				
			} catch (err) {
				console.log('err', err);
			}
		},
		async resetAssignment() {
			try {
				this.resetting = true;
				this.canSave = false;
				this.canCancel = false;
				
				// load people from db
				await this.$store.dispatch('users/dbUsers');
				
				this.resetting = false;
				
			} catch (err) {
				console.log('err', err);
			}			
		},

	},
}
</script>

<style lang="scss">
#assignment {
	&.group {
		div#ctrl {
			button:first-of-type {
				margin-left: auto;
			}
		}		
	}
}
</style>
